import type { MultiselectToggleGroup } from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { getPrimaryAnswer, createEmptyFlattenResult } from "../utils";
import { createShadowQuestion } from "../utils";
import { SecondaryText } from "../utils/SecondaryText";

/**
 * consolidated_boolean_group
 * Includes the primary question, boolean group questions, and a shadow question summarizing visible questions.
 */
export const flattenMultiselectToggleGroup = (
  group: MultiselectToggleGroup,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, booleanGroups } = group;
  /*
    TODO: At the moment we are taking the booleanGroups array and flattening them, and creating a shadow question
    from all the questions in all the boolean groups. This works for our current use where we expect the array to have length 1,
    but if we ever have multiple boolean groups in a multiselect toggle group, we will need to amend this logic.
  */
  const allBooleanQuestions = booleanGroups
    .flatMap(bg => bg.groupQuestions)
    .filter(isQuestion);

  // If the primary or group questions are missing, or all the boolean questions are hidden, return an empty flatten group
  if (
    !primaryQuestion ||
    !booleanGroups ||
    allBooleanQuestions.every(q => q.visible === false)
  ) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);
  const primaryIsHidden = primaryQuestion.visible === false;

  const visibleBooleanQuestions = allBooleanQuestions.filter(q => q.visible);

  const lastItemDisables = booleanGroups.some(bg => bg.lastItemDisables);

  // Map questions with answers. Children are hidden but must have explicit "yes"/"no" values.
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };

  const booleanQuestionsWithAnswers = allBooleanQuestions.map(q => {
    if (!primaryIsHidden && primaryAnswer === "no") {
      // When primary is visible and explicitly "no", set all children to "no"
      answersByQuestionName[q.name] = "no";
    } else {
      // Use question's answer, form state, or default to "no"
      answersByQuestionName[q.name] = q.answer ?? answers[q.name] ?? "no";
    }

    visibilityByQuestionName[q.name] = false;

    // Always include answers even when hidden
    return { ...q, includeAnswer: true };
  });

  // When the primary question is not visible, use its primaryText as the shadow title.
  // This restores the original boolean_group title that was replaced by consolidated_title
  // to avoid duplicate text when the primary is visible.
  const shadowTitle =
    primaryIsHidden && primaryQuestion.primaryText
      ? primaryQuestion.primaryText
      : booleanGroups[0].title;

  const shadowQuestion = createShadowQuestion({
    id: group.id,
    title: shadowTitle,
    subText: "",
    visibleQuestions: visibleBooleanQuestions,
    answers: answersByQuestionName,
    required: true,
    lastItemDisables,
  });

  // Update primary question's secondary text with the generated React element or preserve original
  const primaryQuestionWithSecondaryText = {
    ...primaryQuestion,
    secondaryText: SecondaryText({
      primaryQuestion,
      questions: allBooleanQuestions,
      lastItemDisables,
    }),
  };

  return {
    questions: [
      primaryQuestionWithSecondaryText,
      ...booleanQuestionsWithAnswers,
      shadowQuestion.question,
    ],
    groups: {
      [shadowQuestion.question.name]: {
        kind: "shadow",
        names: visibleBooleanQuestions.map(q => q.name),
      },
      [primaryQuestionWithSecondaryText.name]: {
        kind: "group",
        names: [shadowQuestion.question.name],
      },
    },
    visibility: {
      ...visibilityByQuestionName,
      [shadowQuestion.question.name]:
        primaryIsHidden || primaryAnswer === "yes",
    },
    answers: {
      ...answersByQuestionName,
      [primaryQuestionWithSecondaryText.name]: primaryAnswer || "",
      [shadowQuestion.question.name]: shadowQuestion.answer,
    },
  };
};
